import React, { useEffect, useState } from "react";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import { TextAreaField } from "../../../../Components/formElements/TextAreaField";

import ItemService from "../../../../services/customer_appCommonService";
import SuccessModal from "../../../../Components/Modal/supplier_app/SuccessModal";
import { CANCELLABLE_ERF_STATUS_CODES } from "./constants";

const CancelModal = React.forwardRef((props, ref) => {
  const { type, selectedrow, title } = props;

  const [formInputs, setFormInputs] = useState({
    documentNumbers: [],
    statusHeader: "Cancel",
    cancellationRemark: "",
  });
  const [errorMsg, setErrorMsg] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    setFormInputs((prev) => ({
      ...prev,
      documentNumbers: selectedrow?.map((item) => item?.documentNumber) || [],
    }));
  }, [selectedrow]);

  /* success modal */
  const successModalData = { title: "Message", successMsg: "" };
  const [getShowSuccessModal, setShowSuccessModal] = useState(false);
  const [getsucceModalData, setsucceModalData] = useState(successModalData);

  const onSuccessmodel = () => setShowSuccessModal(true);

  const hideSuccessModal = () => {
    setShowSuccessModal(false);
    if (props.onSuccess) {
      props.onSuccess();
    }
  };

  const onInputChange = ({ target: { name, value } }) => {
    setFormInputs((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formInputs.cancellationRemark.trim()) {
      setErrorMsg("*Please enter the remarks");
      return;
    }
    if (!formInputs.documentNumbers.length) {
      setErrorMsg("*No ERF selected to cancel");
      return;
    }

    setErrorMsg("");
    setIsProcessing(true);

    const documentNumber = formInputs.documentNumbers[0];

    try {
      // ── STEP 1: ERF Detail fetch (authoritative status + assigned barcodes) ──
      const detailRes = await ItemService.generateQuery({
        queryName: "ERFDetail",
        page: 1,
        per_page: 1,
        timeZone: "Asia/Calcutta",
        documentNumber,
      });

      if (!(detailRes?.status === 200 && detailRes?.data?.length > 0)) {
        setErrorMsg("Could not load ERF details. Please try again.");
        return;
      }

      const erfData = detailRes.data[0];
      const code = (erfData.erfStatusCode || "").toUpperCase();
      const label = erfData.erfStatus || "";

      // ── STEP 1a: STATUS GUARD ──────────────────────────────────────────────
      if (label === "Cancelled" || code === "CLD") {
        setErrorMsg("This ERF is already cancelled.");
        return;
      }
      // ── STATUS GUARD (allowlist / default-deny) ──────────────────────────────
      if (!CANCELLABLE_ERF_STATUS_CODES.includes(code)) {
        setErrorMsg(
          `This ERF is "${label || code}" and cannot be cancelled here. ` +
            `Cancellation is only allowed at "Assign To Store", "Ready To Dispatch", or "Under Service Executive". ` +
            `For dispatched/in-use ERFs, use the Credit Note / Stock Return flow.`,
        );
        return; // finally resets isProcessing
      }

      // Collect assigned barcodes (safe now — pre-dispatch only)
      const allItems = [
        ...(erfData.rentItems || []),
        ...(erfData.saleItems || []),
        ...(erfData.nursingItems || []),
        ...(erfData.serviceItems || []),
        ...(erfData.accessoryItems || []),
      ];
      const assignedBarcodes = allItems
        .filter((item) => item.assignBarcode === true && item.barcode)
        .map((item) => ({
          barcode: item.barcode,
          equipmentItemNr: item.equipmentItemNr || 1,
        }));

      const cancelRes = await ItemService.updateEquipmentRentForm({
        documentNumber,
        status: "Cancelled",
        erfStatus: "Cancelled",
        erfStatusCode: "CLD",
        cancellationRemark: formInputs.cancellationRemark,
        cancellationHistory: [
          {
            remark: formInputs.cancellationRemark,
            cancelledAt: new Date().toISOString(),
          },
        ],
      });

      if (cancelRes?.status !== 200) {
        setErrorMsg(
          cancelRes?.message || "Failed to cancel the ERF. Please try again.",
        );
        return;
      }

      // ── STEP 3: Each barcode → history load → StockIn update ────────────────
      if (assignedBarcodes.length > 0) {
        await Promise.all(
          assignedBarcodes.map(async ({ barcode, equipmentItemNr }) => {
            try {
              const barcodeRes = await ItemService.generateQuery({
                queryName: "MaterilBarcodeList",
                page: 1,
                per_page: 10,
                searchTerm: "",
                rowFilter: { $and: [{ barcode }] },
              });

              const existingHistory =
                barcodeRes?.status === 200 && barcodeRes?.data?.length > 0
                  ? barcodeRes.data[0]?.barcodeExtendHistory ||
                    barcodeRes.data[0]?.barcodeERFHistory ||
                    barcodeRes.data[0]?.barcodeHistory ||
                    []
                  : [];

              const newHistoryEntry = {
                equipmentItemNr,
                equipmentRentNr: documentNumber,
                erfTypeCode: erfData?.erfTypeCode || "",
                status: "UnAssigned",
                erfStatus: "Cancelled",
                updatedAt: new Date().toISOString(),
                updatedBy: "M000",
                updatedByName: "M000",
              };

              await ItemService.updateMaterialBarcodeItem({
                barcode,
                materialStatus: "StockIn",
                barCode: "UnAssigned",
                barCodeAssignedDateTime: new Date().toISOString(),
                barcodeERFHistory: [...existingHistory, newHistoryEntry],
              });
            } catch (err) {
              console.error(`>> Barcode ${barcode} update failed:`, err);
            }
          }),
        );
      }

      successModalData.successMsg =
        cancelRes.message || "ERF Cancelled successfully";
      setsucceModalData(successModalData);
      onSuccessmodel();
    } catch (err) {
      console.error(">> handleSubmit error:", err);
      setErrorMsg("Something went wrong while cancelling. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <Modal
        {...props}
        aria-labelledby="contained-modal-title-vcenter"
        keyboard={false}
        size="lg"
        ref={ref}
        centered
      >
        <Modal.Header>
          <Modal.Title id="contained-modal-title-vcenter">
            <i className="bi bi-plus-lg"></i> {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form className="space-y-4">
            <Row>
              <Col xs={12} md={12} lg={12} className="commLeftRightPadding">
                <div className="fw-medium mb-4">
                  <p>Do you want to cancel the {type} Number?</p>
                  <Row>
                    {formInputs?.documentNumbers.map((item, index) => (
                      <Col xs={5} lg={2} className="me-2" key={index}>
                        <p className="bg-table-status bg-table-new-status mb-1 px-2">
                          {item}
                        </p>
                      </Col>
                    ))}
                  </Row>
                </div>
                <TextAreaField
                  className="inputBox"
                  label=""
                  name="cancellationRemark"
                  placeholder={`Please enter the cancellation remark`}
                  value={formInputs.cancellationRemark}
                  onChange={onInputChange}
                />
              </Col>
            </Row>

            <Row className="justify-end">
              <Col xs={6} md={3} lg={3} className="commLeftRightPadding">
                <button
                  type="button"
                  onClick={() => props.onHide()}
                  className="btn btn-danger mt-4 w-full justify-center rounded-md"
                  disabled={isProcessing}
                >
                  No
                </button>
              </Col>
              <Col xs={6} md={3} lg={3} className="commLeftRightPadding">
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="btn btn-primary mt-4 w-full justify-center rounded-md"
                  disabled={isProcessing}
                >
                  {isProcessing ? "Processing..." : "Yes"}
                </button>
              </Col>
              <Col xs={12} md={12} lg={12} className="commLeftRightPadding">
                <p className="errorMsg">{errorMsg}</p>
              </Col>
            </Row>
          </form>
        </Modal.Body>
      </Modal>

      <SuccessModal
        show={getShowSuccessModal}
        onHide={() => hideSuccessModal()}
        successmodaldata={getsucceModalData}
      />
    </>
  );
});

export default CancelModal;
